import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { 
  ShoppingCart, 
  Inbox,
  PackageCheck,
  Warehouse,
  ClipboardList,
  Building2,
  Package,
  MessageSquare,
  Briefcase,
  Calendar,
  FileText,
  Wrench,
  HardHat,
  AlertTriangle
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PurchasingPanelContent = () => {
  const location = useLocation();
  const { getBadgeCount: getNewBadgeCount, markSeen } = useBadges();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [jobsStats, setJobsStats] = useState(null);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Bildirimler alınamadı:', error);
    }
  }, []);

  // Fetch unread purchased orders count
  const [unreadPurchasedCount, setUnreadPurchasedCount] = useState(0);
  
  const fetchUnreadPurchased = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${BACKEND_URL}/api/purchasing/orders/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadPurchasedCount(data.count || 0);
      }
    } catch (error) {
      console.error('Alınan ürünler sayısı alınamadı:', error);
    }
  }, []);

  const fetchJobsStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${BACKEND_URL}/api/purchasing/jobs/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobsStats(data);
      }
    } catch (error) {
      console.error('İş istatistikleri alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    fetchJobsStats();
    fetchUnreadPurchased();
    const interval = setInterval(() => {
      fetchUnreadCounts();
      fetchJobsStats();
      fetchUnreadPurchased();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts, fetchJobsStats, fetchUnreadPurchased]);

  useEffect(() => {
    fetchUnreadCounts();
    fetchJobsStats();
    fetchUnreadPurchased();
  }, [location.pathname, fetchUnreadCounts, fetchJobsStats, fetchUnreadPurchased]);

  // Get unread jobs count for badge (only unread jobs)
  const getUnreadJobsCount = () => {
    if (!jobsStats) return 0;
    return jobsStats.unread || 0;
  };

  const menuItems = [
    { path: '/purchasing/stock', icon: Package, label: 'Stok Özeti', menuKey: 'stock', badgeKey: null },
    { path: '/purchasing/demirbaslar', icon: ClipboardList, label: 'Demirbaşlar', menuKey: 'demirbaslar', badgeKey: null },
    { path: '/purchasing/global-data', icon: Building2, label: 'Cari & Stok Kartı', menuKey: 'global_data', badgeKey: null },
    { path: '/purchasing/communication', icon: MessageSquare, label: 'İletişim', menuKey: 'communication', badgeKey: 'purchasing_messages' },
    { path: '/purchasing/jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: 'purchasing_inbox', useUnreadJobs: true },
    { path: '/purchasing/offers', icon: FileText, label: 'Teklifler', menuKey: 'offers', badgeKey: null },
    { path: '/purchasing/services', icon: Wrench, label: 'Alınan Hizmetler', menuKey: 'services', badgeKey: null },
    { path: '/purchasing/subcontractors', icon: HardHat, label: 'Taşeronlar', menuKey: 'subcontractors', badgeKey: null },
    { path: '/purchasing/purchased', icon: PackageCheck, label: 'Alınan Ürünler', menuKey: 'purchased', badgeKey: null, useUnreadPurchased: true },
    { path: '/purchasing/warehouses', icon: Warehouse, label: 'Depolar', menuKey: 'warehouses', badgeKey: null },
    { path: '/purchasing/calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const getBadgeCount = (item) => {
    // Önce yeni badge sisteminden kontrol et
    let count = getNewBadgeCount(item.menuKey);
    if (count > 0) return count;
    
    // Eski sistemden kontrol et
    count = 0;
    if (item.useUnreadJobs) {
      count += getUnreadJobsCount();
    }
    if (item.useUnreadPurchased) {
      count += unreadPurchasedCount;
    }
    if (item.badgeKey && unreadCounts[item.badgeKey]) {
      count += unreadCounts[item.badgeKey];
    }
    return count;
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="purchasing-panel">
      {/* Header */}
      <PanelHeader
        title="Satın Alma Paneli"
        titleIcon={ShoppingCart}
        titleColor="text-orange-600"
      />

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const badgeCount = getBadgeCount(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => item.menuKey && markSeen(item.menuKey)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  data-testid={`menu-${item.path.split('/').pop()}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge 
                      variant={item.usePendingJobs ? "default" : "destructive"} 
                      className={`ml-auto h-5 min-w-[20px] rounded-full px-1.5 text-xs flex items-center justify-center ${
                        item.usePendingJobs ? 'bg-orange-500 hover:bg-orange-600' : ''
                      }`}
                    >
                      {badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}

            {/* Uyarılar butonu - menü listesinin en altında (Takvim'in hemen altında) */}
            <Link
              to="/purchasing/warnings"
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/purchasing/warnings')
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-amber-50 hover:text-amber-700'
              }`}
              data-testid="menu-warnings"
            >
              <AlertTriangle className="h-5 w-5" />
              <span>Uyarılar</span>
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const PurchasingPanel = () => {
  return (
    <BadgeProvider panel="purchasing">
      <PurchasingPanelContent />
    </BadgeProvider>
  );
};

export default PurchasingPanel;
